import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ProjectHeader from "../_shared/ProjectHeader/ProjectHeader";
import { ArrowRightIcon, ExecutionBlueprintProgressBar } from "./ExecutionBlueprintHelpers";
import "./ExecutionBlueprint.scss";
import ExecutionBlueprintSteps from "./ExecutionBlueprintSteps";
import ExecutionBlueprintSkeleton from "./ExecutionBlueprintSkeleton";
import { HeaderContext } from "../../../HeaderContext";
import {
  EXECUTION_BLUEPRINT_LIVE_REVIEW,
  EXECUTION_BLUEPRINT_LOCKED_DEFAULT,
  EXECUTION_BLUEPRINT_REVIEW,
} from "../../../data/RouteUrls";
import { StandardTooltip } from "../../shared/StandardTooltip/StandardTooltip";
import useExecutionBlueprintStore, {
  EXW_DESTINATION,
  useExecutionBlueprintStoreSelector,
} from "./_redux/ExecutionBlueprintStore";
import { routeForLiveReviewStep } from "./_redux/liveReviewStepRoutes";

const selectDashboard = (state) => ({
  clients: state.clients,
  dashboardLoading: state.dashboardLoading,
});

const ClientCard = ({
  blueprintId,
  isReviewStage,
  destination,
  liveReviewStep,
  initials,
  name,
  ceo,
  filled,
  statusText,
  statusSubText,
  briefDisabled,
  briefDisabledReason,
  buttonText = "View brief",
  timestamps,
  isOpen,
  onToggle,
}) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // There's no coach-side "Brief" screen for Execution Blueprint the way
  // there is for Strategy Canvas (COACH-API.md has no brief endpoint), so the
  // destination per stage is resolved in the store (`getDestination`) rather
  // than inferred here. Note both review-related stages land on the review
  // screen: Live Review is started from inside it, so REVIEW_SUBMITTED's
  // "Start Live Review" button goes there rather than jumping the gate.
  const goToBrief = async (event) => {
    event.stopPropagation();
    if (briefDisabled) return;

    // Straight to the stage the session has actually reached, when the payload
    // says which one that is. The Live Review screen is only the DEFAULT — it is
    // where `finalize_blueprint` lives and where a row with no step yet belongs.
    //
    // ⚠️ Falls back rather than blocking on a missing or unrecognised step: the
    // Live Review screen still does its own step-routing, so a stale dashboard
    // costs one redirect instead of a dead button.
    if (destination === EXW_DESTINATION.LIVE_REVIEW) {
      history.push(
        routeForLiveReviewStep(liveReviewStep, blueprintId) ||
          EXECUTION_BLUEPRINT_LIVE_REVIEW.replace(":blueprintId", blueprintId),
      );
      return;
    }

    // A locked blueprint opens the read-only record rather than the review —
    // the review's controls are all meaningless once nothing can change. Lands
    // on the first of the three tabs; until 2026-09-02 it landed on a hub in
    // front of them, which was deleted on instruction.
    if (destination === EXW_DESTINATION.LOCKED) {
      history.push(
        EXECUTION_BLUEPRINT_LOCKED_DEFAULT.replace(":blueprintId", blueprintId),
      );
      return;
    }

    if (destination !== EXW_DESTINATION.REVIEW) return;
    // Only worth calling at the stage that actually opens review for the
    // first time; re-posting it after the review was already submitted would
    // be a pointless write.
    if (isReviewStage) {
      await useExecutionBlueprintStore.getState().enterReview(blueprintId);
    }
    history.push(EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId));
  };

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, filled]);

  return (
    <div className={`eb-card ${isOpen ? "open" : ""}`}>
      <div
        className="eb-card-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
      >
        <div className="eb-card-left">
          <div className="eb-avatar">{initials}</div>
          <div className="eb-info">
            <div className="eb-name">{name}</div>
            <div className="eb-ceo">{ceo}</div>
            <div className="eb-progress-row">
              <ExecutionBlueprintProgressBar filled={filled} />
              <div className="eb-status">
                <span className="eb-status-text">{statusText}</span>
                {statusSubText ? (
                  <>
                    <span className="eb-status-divider" />
                    <span className="eb-status-sub">{statusSubText}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="eb-card-actions">
          {/* A disabled CTA has to say why it's disabled rather than sitting
              there dead (CLAUDE.md). The tooltip needs a wrapper element
              because a disabled button swallows the mouse events
              OverlayTrigger listens for. */}
          {briefDisabled && briefDisabledReason ? (
            <StandardTooltip tooltTipContent={briefDisabledReason}>
              <span className="eb-brief-btn-wrap">
                <button className="eb-brief-btn" disabled>
                  {buttonText}
                  <ArrowRightIcon />
                </button>
              </span>
            </StandardTooltip>
          ) : (
            <button
              className="eb-brief-btn"
              disabled={briefDisabled}
              onClick={goToBrief}
            >
              {buttonText}
              <ArrowRightIcon />
            </button>
          )}
        </div>
      </div>
      <div className="eb-accordion" style={{ maxHeight: contentHeight }}>
        <div ref={contentRef} className="eb-accordion-inner">
          <div className="eb-accordion-divider" />
          <ExecutionBlueprintSteps filled={filled} timestamps={timestamps} />
        </div>
      </div>
    </div>
  );
};

// Keep in sync with the $accordion-transition-duration used in
// ExecutionBlueprint.scss for the .eb-accordion max-height transition.
const ACCORDION_TRANSITION_MS = 350;

// Coach dashboard for Execution Blueprint - GET /coach/execution-blueprint/dashboard
// (COACH-API.md §1-2). Same accordion-card design as Strategy Canvas's own
// dashboard (StrategyCanvas.js): one row per client, expandable to show the
// 11-milestone timeline. See CLAUDE.md for why the store/steps logic isn't a
// straight port despite the visual parity.
const ExecutionBlueprint = () => {
  // Only one row is ever open at a time - same reasoning as Strategy
  // Canvas's dashboard: close the current one first and only start opening
  // the new one once that collapse animation has finished, so the newly
  // opened card doesn't appear to jump upward.
  const [openId, setOpenId] = useState(null);
  const openTimerRef = useRef(null);
  const HeaderMetaData = useContext(HeaderContext);
  const { clients, dashboardLoading } =
    useExecutionBlueprintStoreSelector(selectDashboard);
  const loading = dashboardLoading || clients === null;

  useEffect(() => {
    document.title = "Art of Scale | Execution Blueprint";
    HeaderMetaData.setHeaderMetadata({
      headerTitle: "Execution Blueprint",
    });
    useExecutionBlueprintStore.getState().fetchDashboard();
  }, []);

  useEffect(() => {
    return () => clearTimeout(openTimerRef.current);
  }, []);

  const handleToggle = (id) => {
    clearTimeout(openTimerRef.current);

    if (openId === id) {
      setOpenId(null);
      return;
    }

    if (openId === null) {
      setOpenId(id);
      return;
    }

    setOpenId(null);
    openTimerRef.current = setTimeout(() => {
      setOpenId(id);
    }, ACCORDION_TRANSITION_MS);
  };

  return (
    <div>
      <ProjectHeader handleNavigation={() => {}} showBack={false} />
      <div id="execution-blueprint">
        <div className="eb-header">
          <div className="eb-title">Execution Blueprint</div>
          <div className="eb-subtitle">
            {loading
              ? ""
              : `${clients.length} ${clients.length === 1 ? "client" : "clients"}`}
          </div>
        </div>
        {loading ? (
          <ExecutionBlueprintSkeleton count={4} />
        ) : clients.length === 0 ? (
          <div className="eb-empty-state">
            No clients with Execution Blueprint access yet.
          </div>
        ) : (
          <div className="eb-list eb-fade-in">
            {clients.map((client) => (
              <ClientCard
                key={client.id}
                {...client}
                isOpen={openId === client.id}
                onToggle={() => handleToggle(client.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutionBlueprint;
